// The instances list's view of the algo state stream: only what the list
// draws, so it can skip every frame that changes nothing it shows.
import { groupInstances, isLive } from '@/features/options-mm/lib/instances'
import { AlgoState, OptionsMmLifecycle, OptionsMmMode } from '@/types/generated/algo'

export interface InstanceRow {
  id: string
  chain: string
  lifecycle: OptionsMmLifecycle
  mode: OptionsMmMode
  live: boolean
  blocked: boolean
}

export interface InstanceListGroup {
  accountId: string
  rows: InstanceRow[]
  reservedTotal: number
  openOrderCap: number | null
  /** Matching requests/s the group's live instances may spend. */
  budgetShareTotal: number
  /** The account's market-maker rate tier, from the newest reported capabilities; `null` until one reports. */
  marketMakerTier: boolean | null
}

export function instanceListView(states: AlgoState[], includeEnded: boolean): InstanceListGroup[] {
  return groupInstances(states, { includeEnded }).map((g) => {
    // Instances are ordered live first, newest first.
    const caps = g.instances.find((s) => s.algoData?.optionsMm?.capabilities)?.algoData?.optionsMm
      ?.capabilities
    return {
      accountId: g.accountId,
      reservedTotal: g.reservedTotal,
      openOrderCap: g.openOrderCap,
      budgetShareTotal: g.budgetShareTotal,
      marketMakerTier: caps ? caps.isMarketMakerTier : null,
      rows: g.instances.map((s) => {
        const mm = s.algoData?.optionsMm
        return {
          id: s.id,
          chain: mm?.params?.quote?.chain ?? '',
          lifecycle: mm?.lifecycle ?? OptionsMmLifecycle.OPTIONS_MM_LIFECYCLE_UNSPECIFIED,
          mode: mm?.mode ?? OptionsMmMode.OPTIONS_MM_MODE_UNSPECIFIED,
          live: isLive(s),
          blocked: !!mm?.blockedReason,
        }
      }),
    }
  })
}

/** Structural equality of two list views (plain data only). */
export function sameListView(a: InstanceListGroup[], b: InstanceListGroup[]): boolean {
  return a === b || JSON.stringify(a) === JSON.stringify(b)
}
